import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { App as CapacitorApp } from "@capacitor/app";
import { demoSector47 } from "./fixtures/demoSector47";
import { ChipMapScreen } from "./screens/ChipMapScreen";
import { ArcopediaScreen } from "./screens/ArcopediaScreen";
import { ManifestScreen } from "./screens/ManifestScreen";
import { SyncScreen } from "./screens/SyncScreen";
import { LoremineScreen } from "./screens/LoremineScreen";
import { ChipSheet } from "./sheets/ChipSheet";
import { GamebookSheet } from "./sheets/GamebookSheet";
import { InvokeSheet } from "./sheets/InvokeSheet";
import { ContextPreviewSheet } from "./sheets/ContextPreviewSheet";
import { DeltaReviewSheet } from "./sheets/DeltaReviewSheet";
import {
  buildContextPacket,
  canApplyDelta,
  legalProcedures,
  makeDemoDelta,
  type TransientSurface,
} from "./core/invoke";
import { indexedDbStore } from "./storage/indexedDbStore";
import type { Chip } from "./core/chip";
import type { InvokeName } from "./core/route";
import { navIconMap } from "./navigation/NavIcons";
import {
  createSession,
  projectLayout,
  projectManifestRows,
  projectSessionToChips,
  resetSession,
  resolveChoice,
  resolveInteraction,
  sector47ModuleProof,
  serializeSession,
  importSession,
  saveSessionLocal,
  loadSessionLocal,
  clearSessionLocal,
  visibleActions,
  getScene,
  type GameSession,
} from "./gamebook";
import "./theme/deckos.css";

const tabs = ["CHIPMAP", "ARCOPEDIA", "MANIFEST", "SYNC"] as const;
const MODULE = sector47ModuleProof;

/** Vertical slice boots into gamebook mode; demo chips remain available offline. */
const DEFAULT_MODE: "gamebook" | "demo" = "gamebook";

export function App() {
  const [mode] = useState<"gamebook" | "demo">(DEFAULT_MODE);
  const [projectChips] = useState<Chip[]>(demoSector47);
  const [session, setSession] = useState<GameSession>(() => {
    const fresh = createSession(MODULE, "ARCHIVIST-47");
    return loadSessionLocal(MODULE, fresh);
  });
  const [activeChipId, setActiveChipId] = useState(
    mode === "gamebook" ? session.currentSceneId : "cpu-001",
  );
  const [tab, setTab] = useState<(typeof tabs)[number]>("CHIPMAP");
  const [loremineOpen, setLoremineOpen] = useState(false);
  const [surface, setSurface] = useState<TransientSurface>({
    type: "CHIP",
    chipId: mode === "gamebook" ? session.currentSceneId : "cpu-001",
    expanded: true,
  });
  const [persistenceError, setPersistenceError] = useState<string>();
  const [command, setCommand] = useState("");
  const [commandError, setCommandError] = useState<string>();
  const [syncMessage, setSyncMessage] = useState<string>();
  /** Compact by default so primary actions stay above the nav on tall phones. */
  const [literaryExpanded, setLiteraryExpanded] = useState(false);
  const pendingOp = useRef<string | null>(null);

  const gameChips = useMemo(
    () => projectSessionToChips(MODULE, session),
    [session],
  );
  const positions = useMemo(
    () => projectLayout(MODULE, session),
    [session],
  );

  const chips = mode === "gamebook" ? gameChips : projectChips;
  const activeChip =
    chips.find((chip) => chip.id === activeChipId) ?? chips[0];
  const currentScene =
    mode === "gamebook"
      ? getScene(MODULE, session.currentSceneId)
      : undefined;
  const focusScene =
    mode === "gamebook"
      ? getScene(MODULE, activeChipId) ?? currentScene
      : undefined;
  const actions =
    mode === "gamebook" ? visibleActions(MODULE, session) : [];

  const selectedRoute =
    surface.type === "INVOKE"
      ? legalProcedures(activeChip).find(
          (route) => route.invoke === surface.selectedInvoke,
        )
      : undefined;
  const context =
    surface.type === "CONTEXT_PREVIEW"
      ? buildContextPacket(activeChip, surface.invoke)
      : undefined;
  const delta =
    surface.type === "DELTA_REVIEW" || surface.type === "CONFLICT"
      ? surface.delta
      : undefined;
  const stale = Boolean(delta && !canApplyDelta(activeChip, delta));

  const commitSession = (next: GameSession) => {
    setSession(next);
    saveSessionLocal(next);
    setActiveChipId(next.currentSceneId);
    setSurface({ type: "CHIP", chipId: next.currentSceneId, expanded: true });
  };

  const runAction = (actionId: string) => {
    setCommandError(undefined);
    const opId = `ui-${session.actionCount + 1}-${actionId}`;
    if (pendingOp.current === opId) return;
    pendingOp.current = opId;
    const result = resolveChoice(MODULE, session, actionId, opId);
    pendingOp.current = null;
    if (!result.ok) {
      setCommandError(result.error);
      return;
    }
    if (!result.duplicate) commitSession(result.session);
  };

  const runCommand = () => {
    setCommandError(undefined);
    const opId = `cmd-${session.actionCount + 1}-${command}`;
    const result = resolveInteraction(MODULE, session, command, opId);
    if (!result.ok) {
      setCommandError(result.error);
      return;
    }
    setCommand("");
    if (!result.duplicate) commitSession(result.session);
  };

  const closeSurface = () =>
    setSurface({ type: "CHIP", chipId: activeChip.id, expanded: false });
  const back = () => {
    if (surface.type === "CONFLICT" || surface.type === "DELTA_REVIEW")
      return closeSurface();
    if (surface.type === "CONTEXT_PREVIEW")
      return setSurface({
        type: "INVOKE",
        chipId: activeChip.id,
        selectedInvoke: surface.invoke,
      });
    if (surface.type === "INVOKE")
      return setSurface({
        type: "CHIP",
        chipId: activeChip.id,
        expanded: false,
      });
    if (surface.type === "CHIP" && surface.expanded)
      return setSurface({ ...surface, expanded: false });
    setTab("CHIPMAP");
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        back();
      }
    };
    window.addEventListener("keydown", onKey);
    const listener = CapacitorApp.addListener("backButton", back);
    return () => {
      window.removeEventListener("keydown", onKey);
      listener.then((handle) => handle.remove());
    };
  }, [surface, activeChip.id]);

  useEffect(() => {
    if (mode !== "demo") return;
    void indexedDbStore
      .get(activeChip.id)
      .then((stored) => (stored ? undefined : indexedDbStore.put(activeChip)))
      .catch(() => undefined);
  }, [activeChip.id, mode]);

  const apply = async () => {
    if (!delta) return;
    setPersistenceError(undefined);
    const result = await indexedDbStore.applyDeltaAtomic(activeChip, delta);
    if (result.status === "APPLIED") {
      setSurface({ type: "CHIP", chipId: result.chip.id, expanded: false });
    } else if (result.status === "STALE")
      setSurface({ type: "CONFLICT", delta: result.conflict.delta });
    else setPersistenceError(result.reason);
  };

  const loreEntries = session.lore.map((id) => {
    const entry = MODULE.loreEntries[id];
    return {
      id: `LORE-${id}`,
      title: entry?.title ?? id,
      type: "MODULE FICTION // DEVELOPMENT FIXTURE",
      revision: `MOD ${MODULE.version}`,
      summary: entry?.body ?? "",
      tone: "cyan",
      focusChipId: session.currentSceneId,
    };
  });

  const manifestRows = projectManifestRows(session, MODULE.title);

  const root = useMemo(
    () =>
      tab === "CHIPMAP" ? (
        <ChipMapScreen
          mode={mode === "gamebook" ? "gamebook" : "demo"}
          chips={chips}
          activeChipId={activeChipId}
          positions={positions}
          onSelectChip={(id) => {
            setActiveChipId(id);
            setSurface({ type: "CHIP", chipId: id, expanded: true });
          }}
        />
      ) : tab === "ARCOPEDIA" ? (
        <ArcopediaScreen
          gamebookOnly={mode === "gamebook"}
          loreEntries={loreEntries}
          onSelectChip={(id) => {
            setActiveChipId(id);
            setTab("CHIPMAP");
            setSurface({ type: "CHIP", chipId: id, expanded: true });
          }}
        />
      ) : tab === "MANIFEST" ? (
        <ManifestScreen
          rows={mode === "gamebook" ? manifestRows : undefined}
          onOpenLoremine={mode === "gamebook" ? () => setLoremineOpen(true) : undefined}
        />
      ) : (
        <SyncScreen
          gamebook={
            mode === "gamebook"
              ? {
                  moduleId: MODULE.id,
                  moduleVersion: MODULE.version,
                  saveRevision: session.saveRevision,
                  sceneId: session.currentSceneId,
                  ending: session.ending,
                  lastMessage: syncMessage,
                  onSave: () => {
                    saveSessionLocal(session);
                    setSyncMessage(
                      `SAVED REV ${session.saveRevision} // GAME_SESSION ONLY`,
                    );
                  },
                  onExport: () => {
                    const blob = new Blob([serializeSession(session)], {
                      type: "application/json",
                    });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `deckos-game-session-${session.sessionId}.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                    setSyncMessage("EXPORTED INERT GAME SAVE");
                  },
                  onImport: (raw) => {
                    const result = importSession(raw, MODULE, session);
                    if (!result.ok) {
                      setSyncMessage(`IMPORT REJECTED: ${result.error}`);
                      return;
                    }
                    commitSession(result.session);
                    setSyncMessage("IMPORT APPLIED // GAME_SESSION");
                  },
                  onReset: () => {
                    clearSessionLocal();
                    const next = resetSession(MODULE, "ARCHIVIST-47");
                    commitSession(next);
                    setSyncMessage("SESSION RESET // PROJECT STATE UNCHANGED");
                  },
                }
              : undefined
          }
          onLoadStale={
            mode === "demo"
              ? () =>
                  setSurface({
                    type: "CONFLICT",
                    delta: {
                      ...makeDemoDelta(activeChip, "BUILD"),
                      baseRevision: activeChip.revision - 1,
                    },
                  })
              : undefined
          }
        />
      ),
    [
      tab,
      chips,
      activeChipId,
      positions,
      mode,
      loreEntries,
      manifestRows,
      session,
      syncMessage,
      activeChip,
    ],
  );

  return (
    <div class={`deckos ${tab.toLowerCase()}${mode === "gamebook" ? " gamebook-mode" : ""}`}>
      <header class="topbar">
        <div>
          DeckOS <strong>0.1</strong> Beta <span class="meta">|</span>{" "}
          <span class="meta">
            {mode === "gamebook" ? "Sector 47 Gamebook" : "Sector 47"}
          </span>
        </div>
        <div>
          <span class="status-dot" />
          {mode === "gamebook" ? "LOCAL MODULE" : "Online"}
        </div>
      </header>
      {root}
      {tab === "CHIPMAP" && mode === "gamebook" && focusScene && (
        <GamebookSheet
          scene={focusScene}
          actions={
            focusScene.id === session.currentSceneId ? actions : []
          }
          transcript={session.transcript}
          command={command}
          error={commandError}
          ending={
            focusScene.id === session.currentSceneId ? session.ending : null
          }
          expanded={literaryExpanded}
          onToggleExpanded={() => setLiteraryExpanded((v) => !v)}
          onCommandChange={setCommand}
          onSubmitCommand={runCommand}
          onAction={runAction}
        />
      )}
      {loremineOpen && mode === "gamebook" && (
        <div class="modal-backdrop" role="presentation">
          <div class="deckos-modal">
            <button type="button" class="deck-chamfer-button" onClick={() => setLoremineOpen(false)}>CLOSE GM TOOL</button>
            <LoremineScreen module={MODULE} session={session} onSession={commitSession} />
          </div>
        </div>
      )}
      {tab === "CHIPMAP" && mode === "demo" && (
        <ChipSheet
          chip={activeChip}
          onInvoke={() =>
            setSurface({
              type: "INVOKE",
              chipId: activeChip.id,
              selectedInvoke: "BUILD",
            })
          }
        />
      )}
      {surface.type !== "CHIP" && mode === "demo" && (
        <div class="modal-backdrop" aria-hidden="true" />
      )}
      {surface.type === "INVOKE" && mode === "demo" && (
        <InvokeSheet
          chip={activeChip}
          routes={legalProcedures(activeChip)}
          selected={selectedRoute}
          onPick={(route) =>
            setSurface({
              type: "INVOKE",
              chipId: activeChip.id,
              selectedInvoke: route.invoke as InvokeName,
            })
          }
          onContinue={() =>
            selectedRoute?.invoke &&
            setSurface({
              type: "CONTEXT_PREVIEW",
              chipId: activeChip.id,
              invoke: selectedRoute.invoke,
            })
          }
          onClose={closeSurface}
        />
      )}
      {surface.type === "CONTEXT_PREVIEW" && context && mode === "demo" && (
        <ContextPreviewSheet
          chipId={activeChip.id}
          invoke={surface.invoke}
          revision={activeChip.revision}
          goal={activeChip.goal}
          bytes={context.bytes}
          known={activeChip.known}
          open={activeChip.open}
          imprints={activeChip.imprints}
          onBack={back}
          onConfirm={() =>
            setSurface({
              type: "DELTA_REVIEW",
              delta: makeDemoDelta(activeChip, surface.invoke),
            })
          }
        />
      )}
      {persistenceError && (
        <div class="failure-banner" role="alert">
          {persistenceError}
        </div>
      )}
      {delta && mode === "demo" && (
        <DeltaReviewSheet
          delta={delta}
          stale={stale || surface.type === "CONFLICT"}
          onReject={closeSurface}
          onApply={apply}
          onResolve={() => setSurface({ type: "CONFLICT", delta })}
        />
      )}
      {
        <nav class="nav" aria-label="DeckOS navigation">
          {tabs.map((name) => {
            const Icon = navIconMap[name];
            return (
              <button
                key={name}
                type="button"
                data-active={tab === name}
                onClick={() => {
                  setTab(name);
                  if (mode === "demo") closeSurface();
                }}
              >
                <Icon active={tab === name} />
                <span class="nav-label">{name}</span>
              </button>
            );
          })}
        </nav>
      }
    </div>
  );
}
